import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Truck, ShieldCheck, AlertTriangle, ChevronRight, ArrowLeft,
  CheckCircle2, XCircle, Clock, FileText, Loader2, AlertCircle, ExternalLink, X,
  Banknote, KeyRound, Copy, Check,
} from "lucide-react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import AdminLiveJobsPanel from "@/components/admin/AdminLiveJobsPanel";
import AdminLiveShiftsPanel from "@/components/admin/AdminLiveShiftsPanel";
import AdminManagedDirectoryPanel from "@/components/admin/AdminManagedDirectoryPanel";
import AdminBulkInvitePanel from "@/components/admin/AdminBulkInvitePanel";
import AdminMarketingPanel from "@/components/admin/AdminMarketingPanel";
import AdminMarketingKitPanel from "@/components/admin/AdminMarketingKitPanel";
import AdminDuplicatesPanel from "@/components/admin/AdminDuplicatesPanel";
import AdminHeldJobsPanel from "@/components/admin/AdminHeldJobsPanel";
import AdminBroadcastPanel from "@/components/admin/AdminBroadcastPanel";
import AdminAutoChargeDryRunPanel from "@/components/admin/AdminAutoChargeDryRunPanel";
import AdminBlogPanel from "@/components/admin/AdminBlogPanel";
import DisputesSection from "@/components/disputes/DisputesSection";

const STATUS_META = {
  pending: { Icon: Clock, classes: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40", label: "Pending" },
  approved: { Icon: CheckCircle2, classes: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40", label: "Approved" },
  rejected: { Icon: XCircle, classes: "bg-[#B91C1C]/10 text-[#B91C1C] border-[#FCA5A5]", label: "Rejected" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const I = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold ${m.classes}`}>
      <I size={12} />
      {m.label}
    </span>
  );
}

function EscrowDrilldownModal({ onClose }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/admin/escrow-jobs");
        setData(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmtGBP = (p) => `£${((p || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const STATUS_LABELS = {
    paid: { label: "Funds locked", cls: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40" },
    in_progress: { label: "In progress", cls: "bg-[#1E4D55]/10 text-[#1E4D55] border-[#1E4D55]/40" },
    ready_for_completion: { label: "Awaiting customer", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  };

  return (
    <div
      data-testid="escrow-drilldown-modal"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-5xl w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Escrow ledger</p>
            <h3 className="font-display mt-2 text-3xl font-black tracking-tighter text-[#0A0A0A]">
              Funds in escrow
              {data && <span className="ml-3 text-[#5BB247]">{fmtGBP(data.total_held_pence)}</span>}
            </h3>
          </div>
          <button onClick={onClose} data-testid="escrow-drilldown-close" className="p-2 hover:bg-[#F5F5F5]">
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div data-testid="escrow-drilldown-loading" className="inline-flex items-center gap-2 text-sm text-[#475569]">
            <Loader2 size={14} className="animate-spin" /> Loading&hellip;
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        {data && data.jobs.length === 0 && (
          <div data-testid="escrow-drilldown-empty" className="bg-white border border-[#0A0A0A]/10 border-dashed p-10 text-center">
            <ShieldCheck size={20} className="mx-auto text-[#475569]" />
            <p className="mt-3 font-display text-lg font-bold text-[#0A0A0A]">No funds held right now.</p>
            <p className="mt-1 text-sm text-[#475569]">
              Once customers pay into escrow, those jobs will appear here.
            </p>
          </div>
        )}

        {data && data.jobs.length > 0 && (
          <div className="border border-[#0A0A0A]/10" data-testid="escrow-drilldown-list">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-left text-[11px] uppercase tracking-[0.14em] font-bold text-[#475569]">
                <tr>
                  <th className="p-3">Job</th>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Driver</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.jobs.map((j) => {
                  const meta = STATUS_LABELS[j.status] || { label: j.status, cls: "" };
                  return (
                    <tr key={j.id} data-testid={`escrow-job-row-${j.id}`} className="border-t border-[#0A0A0A]/10">
                      <td className="p-3">
                        <p className="font-semibold text-[#0A0A0A]">{j.title}</p>
                        <p className="text-xs text-[#475569]">{j.category_label}{j.scheduled_date ? ` · ${j.scheduled_date}` : ""}</p>
                      </td>
                      <td className="p-3 text-[#475569]">
                        <p className="text-[#0A0A0A]">{j.customer_name || "—"}</p>
                        <p className="text-xs">{j.customer_email}</p>
                      </td>
                      <td className="p-3 text-[#475569]">
                        <p className="text-[#0A0A0A]">{j.driver_name || "—"}</p>
                        <p className="text-xs">{j.driver_email}</p>
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="p-3 text-right font-display text-lg font-black tracking-tighter text-[#1E4D55]">
                        {fmtGBP(j.amount_pence)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}



function BankPill({ driver }) {
  if (!driver.stripe_connected) {
    return (
      <span data-testid={`bank-pill-not-started-${driver.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569] border border-[#475569]/30 bg-[#475569]/5 px-2 py-0.5">
        <Banknote size={10} /> Bank: not started
      </span>
    );
  }
  if (driver.stripe_charges_enabled && driver.stripe_payouts_enabled) {
    return (
      <span data-testid={`bank-pill-active-${driver.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#2c8642] border border-[#5BB247]/40 bg-[#5BB247]/10 px-2 py-0.5">
        <Banknote size={10} /> Bank connected
      </span>
    );
  }
  return (
    <span data-testid={`bank-pill-pending-${driver.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#A66800] border border-[#F5A623]/40 bg-[#F5A623]/10 px-2 py-0.5">
      <Banknote size={10} /> Bank: action needed
    </span>
  );
}

export default function AdminDashboard() {
  const { driverId } = useParams();
  return driverId ? <AdminDriverDetail driverId={driverId} /> : <AdminQueue />;
}

function AdminQueue() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("pending");
  const [driverType, setDriverType] = React.useState("all"); // v23.23 — self_employed | managed | all
  const [error, setError] = React.useState("");
  const [showEscrowDrill, setShowEscrowDrill] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [driversRes, statsRes] = await Promise.all([
        apiClient.get(`/admin/drivers?status=${filter}&driver_type=${driverType}`),
        apiClient.get(`/admin/stats`),
      ]);
      setDrivers(driversRes.data.drivers || []);
      setStats(statsRes.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [filter, driverType]);

  React.useEffect(() => { load(); }, [load]);

  const queueStats = React.useMemo(() => {
    const verified = drivers.filter((d) => d.verified).length;
    const pendingDocs = drivers.reduce((sum, d) => sum + d.pending_doc_count, 0);
    return { drivers: drivers.length, verified, pendingDocs };
  }, [drivers]);

  const fmtGBP = (pence) => `£${((pence || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <DashboardShell eyebrow="Admin · Verification queue" title="Drivers awaiting review.">
      {/* Money counters — escrow + revenue (v12+) */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[#0A0A0A]/10 mb-4">
          {[
            { label: "Funds in escrow", value: fmtGBP(stats.escrow_held_pence), caption: `${stats.escrow_held_jobs} active job${stats.escrow_held_jobs === 1 ? "" : "s"}`, Icon: ShieldCheck, accent: true, testid: "admin-stat-escrow", clickable: true, onClick: () => setShowEscrowDrill(true) },
            { label: "Revenue · last 7d", value: fmtGBP(stats.revenue_week_pence), caption: `${stats.completed_week} job${stats.completed_week === 1 ? "" : "s"} completed`, Icon: Banknote, accent: false, testid: "admin-stat-revenue-week" },
            { label: "Revenue · lifetime", value: fmtGBP(stats.revenue_lifetime_pence), caption: `${fmtGBP(stats.gross_lifetime_pence)} gross · ${stats.completed_lifetime} jobs`, Icon: Banknote, accent: false, testid: "admin-stat-revenue-lifetime" },
            { label: "Open jobs", value: stats.jobs_open, caption: `${stats.waitlist_total} on waitlist`, Icon: FileText, accent: false, testid: "admin-stat-open-jobs" },
          ].map((s) => {
            const I = s.Icon;
            const Wrapper = s.clickable ? "button" : "div";
            return (
              <Wrapper
                key={s.label}
                type={s.clickable ? "button" : undefined}
                onClick={s.onClick}
                data-testid={s.testid}
                className={`border-r border-b border-[#0A0A0A]/10 p-6 text-left ${s.accent ? "bg-[#1E4D55] text-white" : "bg-white text-[#0A0A0A]"} ${s.clickable ? "hover:bg-[#163C42] transition-colors cursor-pointer" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <I size={18} className={s.accent ? "text-[#5BB247]" : "text-[#1E4D55]"} />
                  <span className={`text-[11px] uppercase tracking-[0.18em] font-bold ${s.accent ? "text-white/70" : "text-[#475569]"}`}>
                    {s.label}{s.clickable ? " ›" : ""}
                  </span>
                </div>
                <p className="mt-6 font-display text-3xl lg:text-4xl font-black tracking-tighter">{s.value}</p>
                {s.caption && (
                  <p className={`mt-1 text-xs ${s.accent ? "text-white/70" : "text-[#475569]"}`}>{s.caption}</p>
                )}
              </Wrapper>
            );
          })}
        </div>
      )}

      {showEscrowDrill && <EscrowDrilldownModal onClose={() => setShowEscrowDrill(false)} />}

      {/* v20 — Live Jobs Panel (drivers currently clocked-in) */}
      <AdminLiveJobsPanel />

      {/* v23.21 — Live driver shifts (rota / timesheet) */}
      <AdminLiveShiftsPanel />

      {/* v23.23 — Managed driver directory (Raanah team members + today's shift status) */}
      <AdminManagedDirectoryPanel />

      {/* v23.24 — Bulk managed-driver invites (magic-link signup for team members) */}
      <AdminBulkInvitePanel />

      {/* v21 — Marketing + Analytics (traffic / funnel / promo / SEO) */}
      <AdminMarketingPanel />

      {/* v22 — Marketing kit & Staff ID cards (QR + brand assets + printable IDs) */}
      <AdminMarketingKitPanel />

      {/* v23.4 — Duplicate accounts cleanup */}
      <AdminDuplicatesPanel />

      {/* v23.8 — Held jobs (risky customers auto-quarantined) — placed prominently at top */}
      <AdminHeldJobsPanel />

      {/* v23.14 — Disputes inbox */}
      <DisputesSection role="admin" />

      {/* v23.14 — Resend marketing broadcasts */}
      <AdminBroadcastPanel />

      {/* v23.19 — Blog editor (SEO content) */}
      <AdminBlogPanel />

      {/* v23.15 — Per-job auto-charge dry-run toggle */}
      <AdminAutoChargeDryRunPanel />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[#0A0A0A]/10 mb-12">
        {[
          { label: "Drivers · total", value: stats?.drivers_total ?? "—", Icon: Truck, accent: false },
          { label: "Drivers · verified", value: stats?.drivers_verified ?? "—", Icon: ShieldCheck, accent: false },
          { label: "Bank connected", value: stats?.drivers_bank_connected ?? "—", Icon: Banknote, accent: false },
          { label: "Pending docs", value: stats?.pending_docs ?? "—", Icon: Clock, accent: true },
        ].map((s) => {
          const I = s.Icon;
          return (
            <div
              key={s.label}
              data-testid={`admin-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-+|-+$/g, "")}`}
              className={`border-r border-b border-[#0A0A0A]/10 p-6 ${s.accent ? "bg-[#1E4D55] text-white" : "bg-white text-[#0A0A0A]"}`}
            >
              <div className="flex items-start justify-between">
                <I size={18} className={s.accent ? "text-[#5BB247]" : "text-[#1E4D55]"} />
                <span className={`text-[11px] uppercase tracking-[0.18em] font-bold ${s.accent ? "text-white/70" : "text-[#475569]"}`}>
                  {s.label}
                </span>
              </div>
              <p className="mt-6 font-display text-4xl font-black tracking-tighter">{s.value}</p>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">
          Driver queue
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <div role="tablist" aria-label="Driver type" className="inline-flex border border-[#0A0A0A]/15 bg-white" data-testid="admin-driver-type-tabs">
            {[
              { key: "all", label: "All types" },
              { key: "self_employed", label: "Self-employed" },
              { key: "managed", label: "Managed" },
            ].map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={driverType === t.key}
                data-testid={`admin-driver-type-${t.key}`}
                onClick={() => setDriverType(t.key)}
                className={`px-4 py-2 text-sm font-semibold tracking-tight transition-colors ${
                  driverType === t.key ? "bg-[#1E4D55] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div role="tablist" aria-label="Verification status" className="inline-flex border border-[#0A0A0A]/15 bg-white">
            {["pending", "verified", "all"].map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                data-testid={`admin-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-sm font-semibold tracking-tight capitalize transition-colors ${
                  filter === f ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="text-[#475569] text-sm inline-flex items-center gap-2" data-testid="admin-queue-loading">
          <Loader2 size={14} className="animate-spin" /> Loading drivers…
        </div>
      ) : drivers.length === 0 ? (
        <div className="bg-white border border-[#0A0A0A]/10 border-dashed p-10 text-center" data-testid="admin-queue-empty">
          <p className="font-display text-xl font-bold text-[#0A0A0A]">
            No drivers in this view.
          </p>
          <p className="mt-2 text-[#475569]">
            {filter === "pending"
              ? "All drivers verified or no drivers signed up yet."
              : filter === "verified"
              ? "No drivers verified yet — check pending."
              : "No driver accounts exist."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]/10" data-testid="admin-driver-list">
          {drivers.map((d) => (
            <button
              key={d.id}
              onClick={() => navigate(`/dashboard/admin/drivers/${d.id}`)}
              data-testid={`admin-driver-row-${d.id}`}
              className="w-full text-left px-6 py-5 border-b last:border-b-0 border-[#0A0A0A]/10 hover:bg-[#F5F5F5] transition-colors flex items-center gap-4"
            >
              <div className="w-10 h-10 bg-[#1E4D55] text-white flex items-center justify-center font-display font-bold text-sm shrink-0">
                {(d.name || d.email).slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0A0A0A] truncate inline-flex items-center gap-2">
                  <span className="truncate">{d.name || d.email}</span>
                  {d.driver_type === "managed" && (
                    <span data-testid={`admin-driver-pill-managed-${d.id}`} className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#1E4D55] bg-[#1E4D55]/10 border border-[#1E4D55]/30 px-1.5 py-0.5">
                      Managed
                    </span>
                  )}
                </p>
                <p className="text-xs text-[#475569] truncate">
                  {d.email}{d.phone ? ` · ${d.phone}` : ""}
                </p>
              </div>
              <div className="hidden sm:flex flex-col items-end gap-1">
                <span className={`text-[11px] uppercase tracking-[0.14em] font-bold ${d.verified ? "text-[#2c8642]" : "text-[#A66800]"}`}>
                  {d.verified ? "Verified" : "Not verified"}
                </span>
                <span className="text-xs text-[#475569]">
                  {d.approved_doc_count}/{d.required_total} required · {d.pending_doc_count} pending
                </span>
                <BankPill driver={d} />
              </div>
              <ChevronRight size={18} className="text-[#475569] shrink-0" />
            </button>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}

function AdminResetLinkCard({ userId, email }) {
  const [busy, setBusy] = React.useState(false);
  const [link, setLink] = React.useState("");
  const [expiresIn, setExpiresIn] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [error, setError] = React.useState("");

  const generate = async () => {
    setBusy(true); setError(""); setCopied(false);
    try {
      const { data } = await apiClient.post(`/admin/users/${userId}/generate-reset-link`);
      setLink(data.reset_url);
      setExpiresIn(data.expires_in_minutes);
      // Best-effort clipboard copy
      try {
        await navigator.clipboard.writeText(data.reset_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch { /* clipboard may be locked down */ }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't generate a reset link");
    } finally { setBusy(false); }
  };

  const copyAgain = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
  };

  return (
    <div className="mb-8 bg-[#1E4D55]/5 border border-[#1E4D55]/30 p-5" data-testid="admin-reset-link-card">
      <div className="flex items-start gap-3 flex-wrap">
        <KeyRound size={20} className="text-[#1E4D55] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[220px]">
          <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
            Driver locked out?
          </p>
          <p className="mt-1 text-sm text-[#475569]">
            Generate a fresh password-reset link and send it via WhatsApp/SMS. We&apos;ll also email {email} as a backup.
            The link stays valid for {expiresIn ? `${expiresIn} minutes` : "60 minutes"}.
          </p>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          data-testid="admin-generate-reset-link-button"
          className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
          {link ? "Rotate & copy again" : "Send login link"}
        </button>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="admin-reset-link-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {link && (
        <div className="mt-4 space-y-2" data-testid="admin-reset-link-block">
          <div className="bg-white border border-[#0A0A0A]/10 p-2 text-[11px] font-mono break-all text-[#0A0A0A]" data-testid="admin-reset-link-url">
            {link}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={copyAgain}
              data-testid="admin-reset-link-copy"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] font-bold text-[#1E4D55] hover:text-[#163C42]"
            >
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy again</>}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Hi, here's your Raanah password reset link — valid for ${expiresIn || 60} minutes: ${link}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-reset-link-whatsapp"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] font-bold text-[#1E4D55] hover:text-[#163C42]"
            >
              <ExternalLink size={12} /> Send via WhatsApp
            </a>
          </div>
          <p className="text-[11px] text-[#475569]">
            Handing this link to anyone else lets them reset the account&apos;s password. Only share with the account holder.
          </p>
        </div>
      )}
    </div>
  );
}

function AdminDriverDetail({ driverId }) {
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [reviewing, setReviewing] = React.useState(null); // {doc, decision}
  const [previewing, setPreviewing] = React.useState(null); // doc id

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/drivers/${driverId}`);
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load driver");
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  React.useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <DashboardShell eyebrow="Admin" title="Loading driver…">
        <Loader2 size={20} className="animate-spin text-[#1E4D55]" />
      </DashboardShell>
    );
  }
  if (error) {
    return (
      <DashboardShell eyebrow="Admin" title="Driver not found">
        <p className="text-[#475569]">{error}</p>
        <Link to="/dashboard/admin" className="mt-4 inline-flex items-center gap-2 text-[#1E4D55] font-semibold">
          <ArrowLeft size={14} /> Back to queue
        </Link>
      </DashboardShell>
    );
  }

  const { driver, documents, required_total, missing_required_types = [], missing_required_labels = [], approved_required_types = [] } = data;
  // v23.20 — Only count approved docs whose type is REQUIRED, so the header
  // matches the backend's verification rule exactly. Previously we counted ANY
  // approved doc including optional ones — leading to "5/5 approved but Not Verified" bug.
  const approvedRequired = approved_required_types.length;

  return (
    <DashboardShell eyebrow="Admin · Driver review" title={driver.name || driver.email}>
      <button
        onClick={() => navigate("/dashboard/admin")}
        data-testid="admin-back-to-queue"
        className="-mt-6 mb-8 inline-flex items-center gap-2 text-sm text-[#1E4D55] font-semibold hover:text-[#163C42]"
      >
        <ArrowLeft size={14} /> Back to queue
      </button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-[#0A0A0A]/10 mb-12">
        <div className="bg-white border-r border-b border-[#0A0A0A]/10 p-6">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">Email</p>
          <p className="mt-3 font-semibold text-[#0A0A0A] break-all" data-testid="admin-driver-email">{driver.email}</p>
        </div>
        <div className="bg-white border-r border-b border-[#0A0A0A]/10 p-6">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">Phone</p>
          <p className="mt-3 font-semibold text-[#0A0A0A]">{driver.phone || "—"}</p>
        </div>
        <div className={`border-r border-b border-[#0A0A0A]/10 p-6 ${driver.verified ? "bg-[#5BB247]/10" : "bg-[#F5A623]/10"}`}>
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">Status</p>
          <p className="mt-3 font-semibold text-[#0A0A0A]" data-testid="admin-driver-status">
            {driver.verified ? "Verified · all required docs approved" : `${approvedRequired} of ${required_total} required approved`}
          </p>
        </div>
      </div>

      <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] mb-6">
        Documents ({documents.length})
      </h2>

      {/* v23.26 — Admin-generated password reset link (email-independent) */}
      <AdminResetLinkCard userId={driverId} email={driver.email} />

      {/* v23.20 — Show admin EXACTLY which required doc types are still missing
          so we never repeat the "5/5 approved but Not Verified" confusion. */}
      {!driver.verified && missing_required_labels.length > 0 && (
        <div className="mb-6 bg-[#F5A623]/10 border-2 border-[#F5A623]/60 p-5" data-testid="admin-missing-required-banner">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#A66800]">
            Waiting on {missing_required_labels.length} required document{missing_required_labels.length > 1 ? "s" : ""}
          </p>
          <p className="mt-1 text-sm text-[#0A0A0A]">
            Driver is NOT yet verified because these required documents are missing or not yet approved:
          </p>
          <ul className="mt-2 list-disc list-inside text-sm text-[#0A0A0A] space-y-0.5">
            {missing_required_labels.map((label, i) => <li key={i}><strong>{label}</strong></li>)}
          </ul>
          <p className="mt-3 text-xs text-[#475569]">
            Ask the driver to upload these from their dashboard. Verification flips automatically the moment all required docs are approved — no separate button needed.
          </p>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="bg-white border border-[#0A0A0A]/10 border-dashed p-10 text-center" data-testid="admin-no-docs">
          <p className="font-display text-xl font-bold text-[#0A0A0A]">No documents uploaded yet.</p>
          <p className="mt-2 text-[#475569]">Driver needs to upload their required documents before review.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              data-testid={`admin-doc-${doc.doc_type}`}
              className="bg-white border border-[#0A0A0A]/10 p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-[#0A0A0A] tracking-tight leading-tight">
                    {doc.label_override || doc.label}
                  </p>
                  <p className="text-[11px] mt-1 text-[#475569]">{doc.doc_type}</p>
                </div>
                <StatusPill status={doc.status} />
              </div>

              <div className="mt-4 text-sm text-[#475569] space-y-1.5">
                <p className="flex items-center gap-2">
                  <FileText size={14} className="text-[#1E4D55]" />
                  <span className="truncate">{doc.file_name}</span>
                </p>
                {doc.document_number && (
                  <p className="text-xs"><span className="uppercase tracking-wider font-bold text-[#0A0A0A] mr-2">Number</span>{doc.document_number}</p>
                )}
                <p className="text-xs">
                  <span className="uppercase tracking-wider font-bold text-[#0A0A0A] mr-2">Valid</span>
                  {doc.valid_from || "—"} → {doc.valid_until || "—"}
                </p>
                {doc.notes && (
                  <p className="text-xs italic">Driver: “{doc.notes}”</p>
                )}
                {doc.admin_notes && (
                  <p className={`text-xs p-2 mt-2 border ${
                    doc.status === "rejected"
                      ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#B91C1C]"
                      : "bg-[#5BB247]/10 border-[#5BB247]/40 text-[#2c8642]"
                  }`}>
                    <span className="font-bold uppercase tracking-wider mr-1">Admin note</span>
                    {doc.admin_notes}
                  </p>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setPreviewing(doc.id)}
                  data-testid={`admin-doc-preview-${doc.doc_type}`}
                  className="inline-flex items-center gap-1.5 border border-[#0A0A0A]/15 hover:border-[#1E4D55] hover:text-[#1E4D55] transition-colors px-3 py-2 text-xs font-semibold text-[#475569]"
                >
                  <ExternalLink size={12} /> Preview
                </button>
                <button
                  onClick={() => setReviewing({ doc, decision: "approved" })}
                  data-testid={`admin-doc-approve-${doc.doc_type}`}
                  className="inline-flex items-center gap-1.5 bg-[#5BB247] text-white hover:bg-[#4A9939] transition-colors px-3 py-2 text-xs font-semibold"
                >
                  <CheckCircle2 size={12} /> Approve
                </button>
                <button
                  onClick={() => setReviewing({ doc, decision: "rejected" })}
                  data-testid={`admin-doc-reject-${doc.doc_type}`}
                  className="inline-flex items-center gap-1.5 bg-[#B91C1C] text-white hover:bg-[#991B1B] transition-colors px-3 py-2 text-xs font-semibold"
                >
                  <XCircle size={12} /> Reject
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {reviewing && (
        <ReviewModal
          doc={reviewing.doc}
          decision={reviewing.decision}
          onClose={() => setReviewing(null)}
          onSaved={async () => {
            setReviewing(null);
            await load();
          }}
        />
      )}

      {previewing && (
        <DocPreview docId={previewing} onClose={() => setPreviewing(null)} />
      )}
    </DashboardShell>
  );
}

function ReviewModal({ doc, decision, onClose, onSaved }) {
  const [notes, setNotes] = React.useState(doc.admin_notes || "");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await apiClient.post(`/admin/documents/${doc.id}/review`, {
        decision,
        admin_notes: notes.trim() || null,
      });
      await onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-review-modal"
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
          {decision === "approved" ? "Approve document" : "Reject document"}
        </p>
        <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">
          {doc.label_override || doc.label}
        </h3>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="admin-review-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
              {decision === "rejected" ? "Reason for rejection" : "Notes (optional)"}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              required={decision === "rejected"}
              placeholder={decision === "rejected" ? "Tell the driver what to fix" : "Anything you'd like to log"}
              data-testid="admin-review-notes"
              className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
            />
          </div>

          {error && (
            <div data-testid="admin-review-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-review-cancel"
              className="px-5 py-3 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-review-confirm"
              className={`inline-flex items-center gap-2 px-5 py-3 font-semibold text-white transition-colors disabled:opacity-60 ${
                decision === "approved" ? "bg-[#5BB247] hover:bg-[#4A9939]" : "bg-[#B91C1C] hover:bg-[#991B1B]"
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Saving…</span>
                </>
              ) : (
                <span>{decision === "approved" ? "Confirm approval" : "Confirm rejection"}</span>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function DocPreview({ docId, onClose }) {
  const [file, setFile] = React.useState(null);
  const [error, setError] = React.useState("");
  const [blobUrl, setBlobUrl] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    apiClient.get(`/admin/documents/${docId}/file`)
      .then(({ data }) => { if (!cancelled) setFile(data); })
      .catch((e) => { if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); });
    return () => { cancelled = true; };
  }, [docId]);

  // v23.3 — Fetch the file as a blob so a 502 (e.g. Cloudinary PDF delivery blocked)
  // surfaces as a real error message instead of the iframe showing "Failed to load PDF".
  React.useEffect(() => {
    if (!file?.file_url || !file.file_url.startsWith("/api/")) return;
    let cancelled = false;
    let createdUrl = null;
    apiClient
      .get(file.file_url, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(data);
        setBlobUrl(createdUrl);
      })
      .catch(async (e) => {
        if (cancelled) return;
        // Axios delivers blob even for error responses — decode it to read the JSON detail
        let detail = "";
        try {
          const txt = e.response?.data instanceof Blob ? await e.response.data.text() : "";
          const parsed = txt ? JSON.parse(txt) : null;
          detail = formatApiErrorDetail(parsed?.detail);
        } catch { /* ignore */ }
        setError(detail || `Could not load document (HTTP ${e.response?.status || "?"})`);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [file?.file_url]);

  const fileSrc = blobUrl
    ? blobUrl
    : (file && file.file_data ? `data:${file.file_mime};base64,${file.file_data}` : null);
  const isPdf = file?.file_mime === "application/pdf";

  return (
    <div
      data-testid="admin-preview-modal"
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#0A0A0A]/10">
          <p className="font-display font-bold text-[#0A0A0A] truncate pr-4">
            {file?.file_name || "Loading…"}
          </p>
          <button
            onClick={onClose}
            data-testid="admin-preview-close"
            aria-label="Close preview"
            className="p-2 hover:bg-[#F5F5F5]"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-[#F5F5F5] p-4">
          {error && (
            <div className="max-w-md text-center" data-testid="admin-preview-error">
              <p className="text-[#B91C1C] text-sm font-semibold whitespace-pre-line">{error}</p>
              <p className="mt-3 text-[11px] text-[#475569]">
                If you&apos;re the Raanah admin: open your Cloudinary console → Settings → Security → tick &quot;Allow delivery of PDF and ZIP files&quot;.
              </p>
            </div>
          )}
          {!error && (!file || !blobUrl) && <Loader2 size={20} className="animate-spin text-[#1E4D55]" />}
          {!error && fileSrc && isPdf && (
            <iframe src={fileSrc} title="Document preview" className="w-full h-[75vh] bg-white" />
          )}
          {!error && fileSrc && !isPdf && (
            <img src={fileSrc} alt="Document preview" className="max-w-full max-h-[75vh] object-contain" />
          )}
        </div>
      </div>
    </div>
  );
}
